import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Package, FileText, Users, BookOpen, 
  LogOut, Menu, X, ChevronRight, BarChart3, CreditCard, Settings, Bell, Megaphone, Calendar, Clock, UserCheck, Newspaper, Plane, Receipt, MessageCircle, ExternalLink, Hotel, BellOff, MapPin
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import NotificationBell from "../NotificationBell";
import BookingCelebrationModal from "../BookingCelebrationModal";
import { usePushNotifications } from "../../hooks/usePushNotifications";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_be106047-7bc1-4755-b814-a58a16500b58/artifacts/p2la4lzy_ChatGPT%20Image%20Dec%2028%2C%202025%2C%2009_16_43%20PM.png";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardLayout = ({ children, type = "agent" }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [chatUnread, setChatUnread] = useState(0);
  const [navCounts, setNavCounts] = useState({ quotes_new: 0, bookings_new: 0, agents_new: 0, hotels_new: 0 });
  const [celebrationBookings, setCelebrationBookings] = useState(null); // null = hidden
  const [chatEnabled, setChatEnabled] = useState(() => localStorage.getItem('chat_feature_enabled') !== 'false');
  const prevBookingsNew = useRef(-1);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { permission, subscribed, loading: pushLoading, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications(token);

  // Listen for chat preference changes from Settings page
  useEffect(() => {
    const handler = () => setChatEnabled(localStorage.getItem('chat_feature_enabled') !== 'false');
    window.addEventListener('chat_pref_changed', handler);
    return () => window.removeEventListener('chat_pref_changed', handler);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!token) return;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API_BASE}/chat/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        setChatUnread(res.data.unread || 0);
      } catch {}
    };
    fetchUnread();
    const poll = setInterval(fetchUnread, 15000);
    return () => clearInterval(poll);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    // Ask for browser notification permission once per session for foreground push
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "default") {
        try { Notification.requestPermission(); } catch {}
      }
    }
    let prevHotels = -1; // sentinel — don't fire on first load
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/nav-counts`, { headers: { Authorization: `Bearer ${token}` } });
        const next = {
          quotes_new: res.data.quotes_new || 0,
          bookings_new: res.data.bookings_new || 0,
          agents_new: res.data.agents_new || 0,
          hotels_new: res.data.hotels_new || 0,
        };
        setNavCounts(next);
        // Trigger celebration popup when admin sees new (unseen) bookings
        if (type === 'admin' && next.bookings_new > 0) {
          const isFirstLoad = prevBookingsNew.current === -1;
          const isIncrease = prevBookingsNew.current !== -1 && next.bookings_new > prevBookingsNew.current;
          if (isFirstLoad || isIncrease) {
            try {
              const res = await axios.get(`${API_BASE}/admin/unseen-bookings`, { headers: { Authorization: `Bearer ${token}` } });
              if (res.data && res.data.length > 0) {
                setCelebrationBookings(res.data);
              }
            } catch {}
          }
        }
        prevBookingsNew.current = next.bookings_new;
        // Web Notification when hotel-requests bucket grows (foreground push)
        if (prevHotels !== -1 && next.hotels_new > prevHotels && typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
          try {
            const n = new Notification("Happy Holidays DMC — Hotel Request Update", {
              body: `You have ${next.hotels_new} pending hotel request${next.hotels_new === 1 ? "" : "s"}.`,
              icon: "/logo192.png",
              tag: "hh-hotels",
            });
            n.onclick = () => { window.focus(); navigate(user?.role === "admin" ? "/admin/hotels" : "/agent/hotels"); };
          } catch {}
        }
        prevHotels = next.hotels_new;
      } catch {}
    };
    fetchCounts();
    const poll = setInterval(fetchCounts, 20000);
    return () => clearInterval(poll);
  }, [token, navigate, user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const agentLinks = [
    { href: "/agent", label: "Dashboard", icon: LayoutDashboard },
    { href: "/agent/request-quote", label: "Request Quote", icon: FileText },
    { href: "/agent/quotes", label: "My Quotes", icon: BookOpen },
    { href: "/agent/bookings", label: "My Bookings", icon: Package },
    { href: "/agent/hotels", label: "Hotel Requests", icon: Hotel },
    { href: "/agent/payments", label: "My Payments", icon: CreditCard },
    { href: "/agent/reports", label: "Reports", icon: BarChart3 },
    { href: "/agent/packages", label: "Packages", icon: Package },
    { href: "/agent/travel-news", label: "Travel News", icon: Newspaper },
    { href: "/agent/currency-rates", label: "Currency Rates", icon: CreditCard },
    { href: "/agent/settings", label: "Settings", icon: Settings },
  ];

  const adminLinks = [
    { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { href: "/admin/packages", label: "Packages", icon: Package, permission: "packages" },
    { href: "/admin/quotes", label: "Quotes", icon: BookOpen, permission: "quotes" },
    { href: "/admin/bookings", label: "Bookings", icon: Package, permission: "bookings" },
    { href: "/admin/hotels", label: "Hotel Requests", icon: Hotel, permission: "bookings" },
    { href: "/admin/payments", label: "Payments", icon: CreditCard, permission: "payments" },
    { href: "/admin/reminders", label: "Reminders", icon: Bell, permission: "payments" },
    { href: "/admin/invoices", label: "Invoices", icon: Receipt, permission: "bookings" },
    { href: "/admin/reports", label: "Reports", icon: BarChart3, permission: "reports" },
    { href: "/admin/destination-report", label: "Destination Report", icon: MapPin, permission: "reports" },
    { href: "/admin/agent-report", label: "Agent P&L Report", icon: Users, permission: "reports" },
    { href: "/admin/resources", label: "Resources", icon: Package, permission: "packages" },
    { href: "/admin/settings", label: "Settings", icon: Settings },
    { href: "/admin/travel-news", label: "Travel News", icon: Newspaper },
    { href: "/admin/currency-rates", label: "Currency Rates", icon: CreditCard },
    { href: "/admin/follow-up", label: "Follow Up", icon: UserCheck },
  ];

  let links = [];
  if (type === "admin") {
    if (user?.role === "admin") {
      links = adminLinks;
    } else {
      const userPerms = user?.permissions || [];
      links = adminLinks.filter(link => {
        if (link.label === "Dashboard" || link.label === "Settings") return true;
        return link.permission && userPerms.includes(link.permission);
      });
    }
  } else {
    links = agentLinks;
  }

  const isActive = (path) => location.pathname === path;

  // Sidebar badge counts per nav item label
  const getBadgeForLabel = (label) => {
    if (type === 'admin') {
      if (label === 'Quotes' && navCounts.quotes_new > 0) return navCounts.quotes_new;
      if (label === 'Bookings' && navCounts.bookings_new > 0) return navCounts.bookings_new;
      if (label === 'Hotel Requests' && navCounts.hotels_new > 0) return navCounts.hotels_new;
      if (label === 'Settings' && navCounts.agents_new > 0) return navCounts.agents_new;
    } else {
      if (label === 'My Quotes' && navCounts.quotes_new > 0) return navCounts.quotes_new;
      if (label === 'Hotel Requests' && navCounts.hotels_new > 0) return navCounts.hotels_new;
    }
    return 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid={`${type}-dashboard-layout`}>
      {/* New Booking Celebration Modal (admin only) */}
      {type === 'admin' && celebrationBookings && (
        <BookingCelebrationModal
          bookings={celebrationBookings}
          onClose={() => {
            setCelebrationBookings(null);
            // Mark as seen when dismissing popup
            if (token) {
              axios.post(`${API_BASE}/admin/mark-bookings-seen`, {}, { headers: { Authorization: `Bearer ${token}` } })
                .then(() => setNavCounts(prev => ({ ...prev, bookings_new: 0 })))
                .catch(() => {});
              prevBookingsNew.current = 0;
            }
          }}
        />
      )}
      {/* Sidebar - Light Theme */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 shadow-sm transform transition-transform duration-300 lg:translate-x-0 lg:static
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-gray-100">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Happy Holidays DMC" className="h-11 w-auto" />
              <div>
                <p className="font-bold text-gray-800 text-sm">Happy Holidays</p>
                <p className="text-xs text-gray-500">DMC Platform</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {links.map((link) => {
              const badge = getBadgeForLabel(link.label);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => {
                    setSidebarOpen(false);
                    // Mark bookings as seen when admin clicks Bookings nav
                    if (type === 'admin' && link.label === 'Bookings' && navCounts.bookings_new > 0 && token) {
                      axios.post(`${API_BASE}/admin/mark-bookings-seen`, {}, { headers: { Authorization: `Bearer ${token}` } })
                        .then(() => setNavCounts(prev => ({ ...prev, bookings_new: 0 })))
                        .catch(() => {});
                    }
                  }}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                    isActive(link.href)
                      ? "bg-emerald-50 text-emerald-700 font-medium border border-emerald-100"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <link.icon className={`w-5 h-5 ${isActive(link.href) ? "text-emerald-600" : "text-gray-400"}`} />
                  <span className="text-sm flex-1">{link.label}</span>
                  {badge > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-semibold leading-none"
                      data-testid={`nav-badge-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
            <div className="px-3 py-2">
              <p className="text-gray-800 font-medium text-sm truncate">{user?.name}</p>
              <p className="text-gray-500 text-xs truncate">{user?.email}</p>
              <div className="mt-2">
                <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                  user?.role === 'admin' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {user?.role === 'employee' ? 'Staff' : user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar - Light Theme */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-emerald-600">Home</Link>
              <ChevronRight className="w-4 h-4 text-gray-300" />
              <span className="text-gray-800 font-medium">{type === "admin" ? "Admin" : "Agent"} Dashboard</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100" data-testid="header-datetime">
              <Calendar className="w-3.5 h-3.5 text-emerald-500" />
              <span className="font-medium text-gray-700">
                {(() => { const d = currentTime; const wk = d.toLocaleDateString("en-IN", { weekday: "short" }); return `${wk}, ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}
              </span>
              <span className="text-gray-300">|</span>
              <Clock className="w-3.5 h-3.5 text-emerald-500" />
              <span className="font-medium text-gray-700 tabular-nums">
                {currentTime.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
              </span>
            </div>
            {type === "admin" && (
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-amber-600 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition-colors border border-amber-200"
                data-testid="header-view-website-btn"
                title="View public website in a new tab"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">View Website</span>
              </a>
            )}
            {chatEnabled && (
              <Link
                to={`/${type}/chat`}
                className="flex items-center gap-1.5 text-emerald-600 hover:bg-emerald-50 px-2.5 py-1.5 rounded-lg transition-colors relative"
                data-testid="header-chat-btn"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">Chat</span>
                {chatUnread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {chatUnread > 9 ? '9+' : chatUnread}
                  </span>
                )}
              </Link>
            )}
            {/* Push notification toggle */}
            <button
              onClick={subscribed ? unsubscribePush : subscribePush}
              disabled={pushLoading}
              title={subscribed ? "Notifications enabled — click to turn off" : "Enable push notifications"}
              data-testid="push-notif-toggle"
              className={`p-1.5 rounded-lg transition-colors ${subscribed ? "text-emerald-600 bg-emerald-50 hover:bg-emerald-100" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
            >
              {subscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
            </button>
            <NotificationBell token={token} lightMode={true} />
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-600 hover:bg-red-50"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-1" />
              <span className="text-sm hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
